import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Link,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { parseSolicitationData } from '../../utils/solicitationParser';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  Description as DescriptionIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { SolicitationAnalysisResult } from '../../types/solicitation';

interface SolicitationResultProps {
  result: SolicitationAnalysisResult;
}

export const SolicitationResult: React.FC<SolicitationResultProps> = ({ result }) => {
  // Parse the raw solicitation data into categorized sections
  const parsedData = parseSolicitationData(result.data);
  const { aiSummary, recommendedActions, similarOpportunities } = result;

  const formatDate = (date: Date | undefined) => {
    if (!date || isNaN(date.getTime())) {
      return 'Date not available';
    }
    try {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date not available';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {parsedData.basicInfo.title}
          </Typography>
          <Tooltip title="Copy URL">
            <IconButton onClick={() => copyToClipboard(parsedData.basicInfo.url)}>
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="body1" paragraph>
          {aiSummary}
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Key Highlights
          </Typography>
          <Grid container spacing={1}>
            {parsedData.keyHighlights.map((highlight, index) => (
              <Grid item key={index}>
                <Chip label={highlight} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Agency Information</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Department"
                        secondary={parsedData.agency.department}
                      />
                    </ListItem>
                    {parsedData.agency.subtier && (
                      <ListItem>
                        <ListItemText
                          primary="Sub-tier"
                          secondary={parsedData.agency.subtier}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List dense>
                    {parsedData.agency.majorCommand && (
                      <ListItem>
                        <ListItemText
                          primary="Major Command"
                          secondary={parsedData.agency.majorCommand}
                        />
                      </ListItem>
                    )}
                    {parsedData.agency.office && (
                      <ListItem>
                        <ListItemText
                          primary="Office"
                          secondary={parsedData.agency.office}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">General Information</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Contract Opportunity Type"
                        secondary={parsedData.generalInfo.contractOpportunityType}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Published Date"
                        secondary={formatDate(parsedData.generalInfo.publishedDate)}
                      />
                    </ListItem>
                    {parsedData.generalInfo.updatedDate && (
                      <ListItem>
                        <ListItemText
                          primary="Updated Date"
                          secondary={formatDate(parsedData.generalInfo.updatedDate)}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Offers Due"
                        secondary={formatDate(parsedData.generalInfo.offersDate)}
                      />
                    </ListItem>
                    {parsedData.generalInfo.inactivePolicy && (
                      <ListItem>
                        <ListItemText
                          primary="Inactive Policy"
                          secondary={parsedData.generalInfo.inactivePolicy}
                        />
                      </ListItem>
                    )}
                    {parsedData.generalInfo.inactiveDate && (
                      <ListItem>
                        <ListItemText
                          primary="Inactive Date"
                          secondary={formatDate(parsedData.generalInfo.inactiveDate)}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Classification</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Set Aside"
                        secondary={parsedData.classification.setAside}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={4}>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="PSC Code"
                        secondary={`${parsedData.classification.pscCode.code} - ${parsedData.classification.pscCode.description}`}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={4}>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="NAICS Code"
                        secondary={`${parsedData.classification.naicsCode.code} - ${parsedData.classification.naicsCode.description}`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Timeline</Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Response Due"
                    secondary={formatDate(parsedData.timeline.responseDate)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Performance Period"
                    secondary={parsedData.timeline.performancePeriod}
                  />
                </ListItem>
                {parsedData.timeline.importantDates.map((date, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={date.description}
                      secondary={formatDate(date.date)}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Location</Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Delivery Location"
                    secondary={parsedData.location.delivery}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Requirements"
                    secondary={parsedData.location.requirements}
                  />
                </ListItem>
                {parsedData.location.restrictions && (
                  <ListItem>
                    <ListItemText
                      primary="Restrictions"
                      secondary={parsedData.location.restrictions}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Contracting Office</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                {parsedData.contacts.contractingOffice.address}
                <br />
                {parsedData.contacts.contractingOffice.city}, {parsedData.contacts.contractingOffice.state} {parsedData.contacts.contractingOffice.zip}
                <br />
                {parsedData.contacts.contractingOffice.country}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Contacts</Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={
                      <>
                        {parsedData.contacts.primary.name}
                        {parsedData.contacts.primary.title && 
                          <Typography variant="caption" display="block">
                            {parsedData.contacts.primary.title}
                          </Typography>
                        }
                      </>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span" display="block">
                          {parsedData.contacts.primary.email}
                        </Typography>
                        <Typography variant="body2" component="span" display="block">
                          {parsedData.contacts.primary.phone}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {parsedData.contacts.secondary && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <>
                          {parsedData.contacts.secondary.name}
                          {parsedData.contacts.secondary.title && 
                            <Typography variant="caption" display="block">
                              {parsedData.contacts.secondary.title}
                            </Typography>
                          }
                        </>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" component="span" display="block">
                            {parsedData.contacts.secondary.email}
                          </Typography>
                          <Typography variant="body2" component="span" display="block">
                            {parsedData.contacts.secondary.phone}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Technical Requirements</Typography>
              </Box>
              <Typography variant="subtitle2" gutterBottom>
                Project Purpose
              </Typography>
              <Typography variant="body2" paragraph>
                {parsedData.technicalDetails.projectPurpose}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Requirements
              </Typography>
              <List dense>
                {parsedData.technicalDetails.requirements.map((req, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={req} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Recommended Actions</Typography>
              </Box>
              <List>
                {recommendedActions.map((action, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={action} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {parsedData.attachments && parsedData.attachments.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DescriptionIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Attachments</Typography>
                </Box>
                <List>
                  {parsedData.attachments.map((attachment, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={
                          <Link href={attachment.url} target="_blank" rel="noopener">
                            {attachment.name}
                          </Link>
                        }
                        secondary={
                          <>
                            {attachment.type && <Typography variant="caption" display="block">{attachment.type}</Typography>}
                            {attachment.description && <Typography variant="caption" display="block">{attachment.description}</Typography>}
                            {attachment.size && <Typography variant="caption" display="block">{Math.round(attachment.size / 1024)} KB</Typography>}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {similarOpportunities && similarOpportunities.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Similar Opportunities
                </Typography>
                <List>
                  {similarOpportunities.map((opp) => (
                    <ListItem key={opp.id}>
                      <ListItemText
                        primary={
                          <Link href={opp.url} target="_blank" rel="noopener">
                            {opp.title}
                          </Link>
                        }
                        secondary={`${Math.round(opp.similarity * 100)}% similar`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
